import {GitHubAvatar} from '@github-ui/github-avatar'
import {hovercardAttributesForActor} from '@github-ui/hovercards'
import PreloadedQueryBoundary from '@github-ui/relay-preloaded-query-boundary'
import {InfoIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'
import {useEffect} from 'react'
import {graphql, type PreloadedQuery, usePreloadedQuery, useQueryLoader} from 'react-relay'

import {renderGenericError} from '../shared/IssueViewerError'
import type {ParticipantsListQuery} from './__generated__/ParticipantsListQuery.graphql'
import styles from './ParticipantsList.module.css'

type ParticipantsListProps = {
  repo: string
  owner: string
  number: number
  totalCount: number
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const ParticipantsListQueryGraphQL = graphql`
  query ParticipantsListQuery($repo: String!, $owner: String!, $number: Int!) {
    repository(name: $repo, owner: $owner) {
      issue(number: $number) {
        participants(first: 100) {
          nodes {
            avatarUrl(size: 64)
            login
            name
          }
        }
      }
    }
  }
`

export function ParticipantsList({repo, owner, number, totalCount}: ParticipantsListProps) {
  const [queryRef, loadParticipants] = useQueryLoader<ParticipantsListQuery>(ParticipantsListQueryGraphQL)

  useEffect(() => {
    loadParticipants({repo, owner, number})
  }, [loadParticipants, number, owner, repo])

  // return null to trigger suspense
  if (!queryRef) return null

  return (
    <PreloadedQueryBoundary
      onRetry={() => loadParticipants({owner, repo, number}, {fetchPolicy: 'network-only'})}
      fallback={renderGenericError}
    >
      <ParticipantsListInternal queryRef={queryRef} totalCount={totalCount} />
    </PreloadedQueryBoundary>
  )
}

function ParticipantsListInternal({
  queryRef,
  totalCount,
}: {
  queryRef: PreloadedQuery<ParticipantsListQuery>
  totalCount: number
}) {
  const {repository} = usePreloadedQuery<ParticipantsListQuery>(ParticipantsListQueryGraphQL, queryRef)
  const fitleredParticipants = (repository?.issue?.participants.nodes || []).flatMap(a => (a ? a : []))

  return (
    <>
      <ActionList>
        {fitleredParticipants.map(participant => {
          const {login, avatarUrl, name} = participant

          return (
            <ActionList.LinkItem key={login} href={`/${login}`} {...hovercardAttributesForActor(login)}>
              <ActionList.LeadingVisual>
                <GitHubAvatar src={avatarUrl} size={20} alt={`@${login}`} className={styles.participantAvatar} />
              </ActionList.LeadingVisual>
              {name}
              <ActionList.Description truncate>{login}</ActionList.Description>
            </ActionList.LinkItem>
          )
        })}
      </ActionList>
      {totalCount > 100 && (
        <div className={styles.participantLimitNotice}>
          <InfoIcon size={16} />
          Only the first 100 participants are shown in this list.
        </div>
      )}
    </>
  )
}

try{ ParticipantsList.displayName ||= 'ParticipantsList' } catch {}
try{ ParticipantsListInternal.displayName ||= 'ParticipantsListInternal' } catch {}